import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark, useBrand } from "@/context/BrandContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Building2, LogIn } from "lucide-react";

function Field({ id, label, testId, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="tx-overline">{label}</Label>
      <Input
        id={id}
        data-testid={testId}
        className="bg-white border-zinc-200 rounded-sm h-10 font-mono text-[13px] focus-visible:ring-orange-500/40"
        {...props}
      />
    </div>
  );
}

export default function Login() {
  const { login, register, status, demo } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", workspace_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (status === "authed" && demo) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-white text-zinc-900 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_520px]" data-testid="page-login">
      <div className="hidden lg:flex flex-col justify-between p-14 border-r border-zinc-200 tx-grid-bg">
        <div className="flex items-center gap-2.5">
          <Wordmark />
        </div>
        <div className="max-w-lg">
          <div className="tx-overline mb-4">Multi-tenant · one workspace per ISV</div>
          <h1 className="font-heading font-black text-5xl leading-[1.05] tracking-tight mb-6">
            {brand.app_name}: every ISV gets its own private-offer engine.
          </h1>
          <p className="text-zinc-600 text-[14px] leading-relaxed">
            An autonomous agent drafts, gates and releases AWS Channel Partner Private Offers —
            fed by {brand.source_app_name} hand-offs. Sign in to your ISV workspace or create a new one.
          </p>
        </div>
        <div className="font-mono text-[11px] text-zinc-400">claude-sonnet-4.5 · AMMP · ACE · MCP</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="flex border border-zinc-200 mb-8" data-testid="auth-mode-tabs">
            {[
              ["login", "Sign in", LogIn],
              ["register", "Create workspace", Building2],
            ].map(([m, label, Icon]) => (
              <button
                type="button"
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 h-10 text-[12px] flex items-center justify-center gap-2 transition-colors ${
                  mode === m ? "bg-zinc-50 text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          <h2 className="font-heading font-black text-2xl mb-6">
            {mode === "login" ? "Welcome back." : "Spin up a new ISV workspace."}
          </h2>

          <div className="space-y-4">
            {mode === "register" && (
              <>
                <Field id="workspace_name" label="ISV workspace name" testId="register-workspace-input"
                       value={form.workspace_name} onChange={set("workspace_name")} placeholder="Acme Software Inc." required />
                <Field id="name" label="Your name" testId="register-name-input"
                       value={form.name} onChange={set("name")} placeholder="Sarah Chen" required />
              </>
            )}
            <Field id="email" label="Email" type="email" testId="auth-email-input"
                   value={form.email} onChange={set("email")} placeholder="you@isv.com" required />
            <Field id="password" label="Password" type="password" testId="auth-password-input"
                   value={form.password} onChange={set("password")} placeholder="••••••••" minLength={8} required />
          </div>

          {error && (
            <div data-testid="auth-error" className="mt-4 border border-rose-500/40 bg-rose-500/5 text-rose-600 text-[12px] px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-button"
            className="w-full mt-6 h-10 bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm gap-2"
          >
            {mode === "login" ? "Sign in" : "Create workspace"}
            <ArrowRight className="w-4 h-4" />
          </Button>

          <p className="mt-6 text-[11px] text-zinc-400 font-mono">
            sessions are JWT · 12h access · 7d refresh
          </p>
        </form>
      </div>
    </div>
  );
}
